import React, { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { TablePagination } from "@mui/material";
import { 
  FaCalendarAlt, FaFileExcel, FaExclamationTriangle,
  FaBuilding, FaUserCheck, FaTimes, FaSearch, FaHistory
} from "react-icons/fa";
import {
  getDailySummary,
  getCenterSummary,
  getCenterLogs,
  triggerManualReport
} from "../../Api/logApi";
import { useLocation } from "react-router-dom";
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600;0,9..144,700;0,9..144,800;1,9..144,500;1,9..144,600&family=IBM+Plex+Mono:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap');

  .dash-root {
    --ink:#101826; --paper:#FFFFFF; --surface:#FFFFFF; --surface-alt:#FFFFFF;
    --brass:#2BB5CE; --brass-soft:#E6F7FA; --brass-deep:#1A869B;
    --teal:#2BB5CE; --teal-soft:#E6F7FA;
    --plum:#1E293B; --plum-soft:#F1F5F9;
    --rose:#B23A48; --rose-soft:#F6DEDF;
    --slate:#6B7686; --hairline:rgba(16,24,38,0.11);
    font-family:'Inter',sans-serif; min-height:100vh; color:var(--ink);
    background-color:#FFFFFF;
  }
  .dash-root * { box-sizing:border-box; }

  .eyebrow {
    font-family:'IBM Plex Mono',monospace; font-size:11px; font-weight:600;
    letter-spacing:0.16em; text-transform:uppercase; color:var(--brass-deep);
    display:flex; align-items:center; gap:8px; margin-bottom:8px;
  }
  .eyebrow::before { content:''; width:16px; height:1px; background:var(--brass); display:inline-block; }

  .section-title { font-family:'Fraunces',serif; font-style:italic; font-size:28px; font-weight:600; color:var(--ink); margin-bottom:4px; letter-spacing:-0.01em; }
  .section-sub   { font-size:13px; color:var(--slate); margin-bottom:26px; }

  .dash-table { width:100%; border-collapse:separate; border-spacing:0; }
  .dash-table thead th {
    background:var(--surface-alt); color:var(--slate); font-family:'IBM Plex Mono',monospace; font-size:10.5px; font-weight:600;
    letter-spacing:0.12em; text-transform:uppercase; padding:14px 18px;
    border-bottom:1px solid var(--hairline);
  }
  .dash-table thead th:first-child { border-radius:14px 0 0 0; }
  .dash-table thead th:last-child  { border-radius:0 14px 0 0; }
  .dash-table tbody tr { transition:background 0.13s ease; }
  .dash-table tbody tr:hover td { background:var(--surface-alt); }
  .dash-table tbody td { padding:13px 18px; border-bottom:1px solid #F1F3F8; color:var(--ink); font-size:14px; background:var(--surface); }
  .dash-table tbody tr:last-child td { border-bottom:none; }
  .dash-table tbody tr:last-child td:first-child { border-radius:0 0 0 14px; }
  .dash-table tbody tr:last-child td:last-child  { border-radius:0 0 14px 0; }

  .back-btn {
    display:inline-flex; align-items:center; gap:8px;
    background:var(--surface); border:1px solid var(--hairline); color:var(--slate);
    padding:8px 16px; border-radius:6px; font-size:12.5px; font-weight:600;
    cursor:pointer; transition:all 0.18s ease;
    font-family:'IBM Plex Mono',monospace; letter-spacing:0.06em; text-transform:uppercase;
    box-shadow:0 1px 3px rgba(16,24,38,0.05); border-bottom:2px solid var(--hairline);
  }
  .back-btn:hover { border-color:var(--hairline); border-bottom-color:var(--brass); color:var(--brass-deep); }

  .input-field {
    background: var(--surface);
    border: 1px solid var(--hairline);
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 13px;
    color: var(--ink);
    font-family: 'Inter', sans-serif;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .input-field:focus {
    border-color: var(--brass);
    box-shadow: 0 0 0 3px var(--brass-soft);
  }

  .stat-card {
    background: var(--surface);
    border: 1px solid var(--hairline);
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(16,24,38,0.04);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`;

const ACTION_COLORS = {
  LOGIN: "bg-blue-50 text-blue-700 border-blue-200",
  LOGOUT: "bg-gray-50 text-gray-700 border-gray-200",
  LOGIN_FAILED: "bg-red-50 text-red-700 border-red-200",
  REGISTRATION_STARTED: "bg-purple-50 text-purple-700 border-purple-200",
  REGISTRATION_COMPLETED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REGISTRATION_ABANDONED: "bg-amber-50 text-amber-700 border-amber-200",
  CANDIDATE_CREATED: "bg-indigo-50 text-indigo-700 border-indigo-200",
  CANDIDATE_UPDATED: "bg-cyan-50 text-cyan-700 border-cyan-200",
  CANDIDATE_APPROVED: "bg-teal-50 text-teal-700 border-teal-200",
  CANDIDATE_DELETED: "bg-rose-50 text-rose-700 border-rose-200",
  AADHAAR_SCAN_STARTED: "bg-yellow-50 text-yellow-700 border-yellow-200",
  AADHAAR_OCR_STARTED: "bg-orange-50 text-orange-700 border-orange-200",
  AADHAAR_OCR_COMPLETED: "bg-green-50 text-green-700 border-green-200",
  AADHAAR_OCR_FAILED: "bg-red-50 text-red-700 border-red-200",
  DL_SCAN_STARTED: "bg-yellow-50 text-yellow-700 border-yellow-200",
  DL_OCR_STARTED: "bg-orange-50 text-orange-700 border-orange-200",
  DL_OCR_COMPLETED: "bg-green-50 text-green-700 border-green-200",
  DL_OCR_FAILED: "bg-red-50 text-red-700 border-red-200",
  BIOMETRIC_STARTED: "bg-sky-50 text-sky-700 border-sky-200",
  BIOMETRIC_COMPLETED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  BIOMETRIC_FAILED: "bg-rose-50 text-rose-700 border-rose-200",
};

const formatDuration = (ms) => {
  if (ms === null || ms === undefined || isNaN(ms)) return "-";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
  }
  return `${seconds}s`;
};

const formatTimeOnly = (dateVal) => {
  if (!dateVal) return "-";
  return new Date(dateVal).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
};

const getTodayKolkataString = () => {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
};

const DailyActivity = () => {
  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState(getTodayKolkataString());
  const [loading, setLoading] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [summary, setSummary] = useState(null);
  const [centers, setCenters] = useState([]);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  
  // Drawer state for specific center activity
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [centerLogs, setCenterLogs] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerSearch, setDrawerSearch] = useState("");
  const [drawerFilterTab, setDrawerFilterTab] = useState("All");
  const [roleFilter, setRoleFilter] = useState("All"); // "All" | "branchUser" | "admin" | "superAdmin"

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const filteredCenters = useMemo(() => {
    return centers.filter(c => {
      if (roleFilter === "All") return true;
      return c.role === roleFilter;
    });
  }, [centers, roleFilter]);

  const paginatedCenters = useMemo(() => {
    return filteredCenters.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredCenters, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [filteredCenters]);

  const loadData = async (date) => {
    setLoading(true);
    try {
      const summaryRes = await getDailySummary(date);
      const centersRes = await getCenterSummary(date);
      setSummary(summaryRes.data.data);
      setCenters(centersRes.data.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load daily activity stats.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    if (!hasAutoOpened && location.state && location.state.autoOpenBranchId && centers.length > 0) {
      const match = centers.find(c => c.branchId === location.state.autoOpenBranchId);
      if (match) {
        setHasAutoOpened(true);
        handleViewCenter(match);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centers, location.state, hasAutoOpened]);

  const handleToday = () => {
    setSelectedDate(getTodayKolkataString());
  };

  const handleYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    setSelectedDate(yesterday.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }));
  };

  const handleManualEmailReport = async () => {
    setSendingReport(true);
    const toastId = toast.loading("Generating Excel report and sending email...");
    try {
      const res = await triggerManualReport(selectedDate);
      toast.success(res.data.message || "Email sent successfully!", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to trigger email report.", { id: toastId });
    } finally {
      setSendingReport(false);
    }
  };

  const handleViewCenter = async (center) => {
    setSelectedCenter(center);
    setDrawerOpen(true);
    setDrawerLoading(true);
    setDrawerSearch("");
    setDrawerFilterTab("All");
    try {
      const res = await getCenterLogs(center.branchId, selectedDate);
      setCenterLogs(res.data.data);
    } catch (err) {
      console.error(err);
      toast.error(`Failed to fetch logs for ${center.name}`);
    } finally {
      setDrawerLoading(false);
    }
  };

  // Filter logs inside Drawer
  const filteredTimelineLogs = useMemo(() => {
    return centerLogs.filter(log => {
      // 1. Text Search
      const searchTxt = drawerSearch.toLowerCase().trim();
      const candIdStr = log.candidate?._id?.toString() || "";
      const candNameStr = log.candidate?.name || log.metadata?.name || "";
      const textMatches = !searchTxt || 
        log.action.toLowerCase().includes(searchTxt) ||
        candIdStr.toLowerCase().includes(searchTxt) ||
        candNameStr.toLowerCase().includes(searchTxt);
      if (!textMatches) return false;

      // 2. Tab Filter
      if (drawerFilterTab === "All") return true;
      if (drawerFilterTab === "Registration") {
        return ["REGISTRATION_STARTED", "REGISTRATION_COMPLETED", "REGISTRATION_ABANDONED", "CANDIDATE_CREATED"].includes(log.action);
      }
      if (drawerFilterTab === "Aadhaar") {
        return log.action.startsWith("AADHAAR_");
      }
      if (drawerFilterTab === "DL") {
        return log.action.startsWith("DL_");
      }
      if (drawerFilterTab === "OCR") {
        return log.action.includes("_OCR_");
      }
      if (drawerFilterTab === "Biometric") {
        return log.action.startsWith("BIOMETRIC_");
      }
      if (drawerFilterTab === "Login") {
        return ["LOGIN", "LOGOUT", "LOGIN_FAILED"].includes(log.action);
      }
      return true;
    });
  }, [centerLogs, drawerSearch, drawerFilterTab]);

  // Aggregate candidate performance table inside Drawer
  const candidatePerformanceData = useMemo(() => {
    const candMap = {};
    centerLogs.forEach(l => {
      const cId = l.candidate?._id?.toString();
      if (!cId) return;
      if (!candMap[cId]) {
        candMap[cId] = {
          candidateId: cId,
          candidateName: l.candidate?.name || "-",
          registrationStart: null,
          registrationEnd: null,
          totalTime: null,
          aadhaarOcrTime: null,
          dlOcrTime: null,
          biometricTime: null,
          status: "In Progress"
        };
      }

      if (l.action === "REGISTRATION_STARTED") {
        candMap[cId].registrationStart = l.createdAt;
      } else if (l.action === "REGISTRATION_COMPLETED") {
        candMap[cId].registrationEnd = l.createdAt;
        candMap[cId].totalTime = l.durationMs;
        candMap[cId].status = "Completed";
      } else if (l.action === "REGISTRATION_ABANDONED") {
        candMap[cId].registrationEnd = l.createdAt;
        candMap[cId].totalTime = l.durationMs;
        candMap[cId].status = "Abandoned";
      } else if (l.action === "AADHAAR_OCR_COMPLETED") {
        candMap[cId].aadhaarOcrTime = l.durationMs;
      } else if (l.action === "DL_OCR_COMPLETED") {
        candMap[cId].dlOcrTime = l.durationMs;
      } else if (l.action === "BIOMETRIC_COMPLETED") {
        candMap[cId].biometricTime = l.durationMs || l.metadata?.durationMs || null;
      }
    });
    return Object.values(candMap);
  }, [centerLogs]);

  return (
    <>
      <style>{styles}</style>
      <div className="dash-root" style={{ padding: "18px 18px" }}>
        
        {/* --- PAGE HEADER --- */}
        <div className="eyebrow">Reports · Activity Logs</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px", marginBottom: "26px" }}>
          <div>
            <h1 className="section-title">Daily Activity & Performance</h1>
            <p className="section-sub" style={{ marginBottom: 0 }}>Monitor real-time operational performance across all centers</p>
          </div>

          {/* Date Selector & Actions */}
          <div className="flex flex-wrap items-center gap-3">
            <button 
              onClick={handleToday}
              className="back-btn"
            >
              Today
            </button>
            <button 
              onClick={handleYesterday}
              className="back-btn"
            >
              Yesterday
            </button>
            
            <div className="relative flex items-center">
              <FaCalendarAlt className="absolute left-3 text-slate-400" />
              <input 
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="input-field"
                style={{ paddingLeft: "36px", height: "39px" }}
              />
            </div>

            <button 
              onClick={handleManualEmailReport}
              disabled={sendingReport || loading}
              style={{
                margin: 0,
                background: "#2BB5CE",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "8px",
                padding: "10px 18px",
                fontWeight: 700,
                fontSize: "13px",
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(43,181,206,0.32)",
                fontFamily: "'IBM Plex Mono', monospace",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
              className="disabled:opacity-50"
            >
              <FaFileExcel />
              <span>Email Excel Report</span>
            </button>
          </div>
        </div>

        {/* --- MAIN SUMMARY CARDS --- */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-10 h-10 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-6">
              <div className="stat-card">
                <span className="text-slate-450 text-[10px] font-bold uppercase tracking-wider">Active Centers</span>
                <span className="text-2xl font-bold text-slate-800 mt-2">
                  {summary?.activeCenters || 0} <span className="text-xs text-slate-400 font-normal">/ {summary?.totalCenters || 0}</span>
                </span>
              </div>

              <div className="stat-card">
                <span className="text-slate-455 text-[10px] font-bold uppercase tracking-wider">Registered</span>
                <span className="text-2xl font-bold text-emerald-600 mt-2">{summary?.candidatesRegistered || 0}</span>
              </div>

              <div className="stat-card">
                <span className="text-slate-450 text-[10px] font-bold uppercase tracking-wider">Aadhaar Scans</span>
                <span className="text-2xl font-bold text-cyan-600 mt-2">{summary?.aadhaarScans || 0}</span>
              </div>

              <div className="stat-card">
                <span className="text-slate-450 text-[10px] font-bold uppercase tracking-wider">DL Scans</span>
                <span className="text-2xl font-bold text-purple-600 mt-2">{summary?.dlScans || 0}</span>
              </div>

              <div className="stat-card">
                <span className="text-slate-450 text-[10px] font-bold uppercase tracking-wider">Total OCR</span>
                <span className="text-2xl font-bold text-indigo-600 mt-2">{summary?.totalOcr || 0}</span>
              </div>

              <div className="stat-card">
                <span className="text-slate-450 text-[10px] font-bold uppercase tracking-wider">Biometrics</span>
                <span className="text-2xl font-bold text-sky-600 mt-2">{summary?.biometricVerifications || 0}</span>
              </div>

              <div className="stat-card">
                <span className="text-slate-450 text-[10px] font-bold uppercase tracking-wider">Avg Reg Time</span>
                <span className="text-base font-bold text-amber-600 mt-2 truncate">
                  {summary && summary.avgCandidateTimeMs ? formatDuration(summary.avgCandidateTimeMs) : "-"}
                </span>
              </div>
            </div>

            {/* Detailed Registration abandonment status */}
            {summary && (summary.registrationStarted > 0 || summary.registrationAbandoned > 0) && (
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6 flex flex-wrap gap-6 items-center text-xs">
                <span className="font-bold text-slate-500 uppercase tracking-wide">Daily Registration Status:</span>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-purple-500"></span>
                  <span>Started: <strong>{summary.registrationStarted}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                  <span>Completed: <strong>{summary.registrationCompleted}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
                  <span>Abandoned: <strong>{summary.registrationAbandoned}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                  <span>Aadhaar OCR Success/Failed: <strong>{summary.aadhaarOcrSuccess} / {summary.aadhaarOcrFailed}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-500"></span>
                  <span>DL OCR Success/Failed: <strong>{summary.dlOcrSuccess} / {summary.dlOcrFailed}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-sky-500"></span>
                  <span>Biometric Success/Failed: <strong>{summary.biometricSuccess} / {summary.biometricFailed}</strong></span>
                </div>
                <div className="ml-auto text-slate-400 font-medium">
                  Fastest: <strong className="text-emerald-600">{summary.fastestCandidateTimeMs ? formatDuration(summary.fastestCandidateTimeMs) : "-"}</strong>
                  {" | "}
                  Median: <strong className="text-cyan-600">{summary.medianCandidateTimeMs ? formatDuration(summary.medianCandidateTimeMs) : "-"}</strong>
                  {" | "}
                  Slowest: <strong className="text-amber-600">{summary.slowestCandidateTimeMs ? formatDuration(summary.slowestCandidateTimeMs) : "-"}</strong>
                </div>
              </div>
            )}

            {/* --- CENTER SUMMARY GRID --- */}
            <div style={{ borderRadius: "6px 6px 16px 16px", overflow: "hidden", border: "1px solid var(--hairline)", boxShadow: "0 2px 12px rgba(16,24,38,0.06)", marginBottom: "26px" }}>
              
              {/* Table header control block */}
              <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <h2 className="font-bold text-slate-700 text-sm uppercase tracking-wider" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Performance Summary</h2>
                
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <label htmlFor="role-filter" className="text-xs font-semibold text-slate-500">Filter Role:</label>
                    <select
                      id="role-filter"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="input-field"
                      style={{ padding: "6px 10px", fontSize: "12px" }}
                    >
                      <option value="All">All Roles</option>
                      <option value="branchUser">Center Staff</option>
                      <option value="admin">System Admin</option>
                      <option value="superAdmin">System Super Admin</option>
                    </select>
                  </div>
                  
                  <span className="px-2 py-1 text-[11px] font-bold bg-cyan-100 text-cyan-800 rounded-full whitespace-nowrap" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    {filteredCenters.length} listed
                  </span>
                </div>
              </div>

              {/* Table markup */}
              <table className="dash-table">
                <thead>
                  <tr>
                    <th style={{ width: "60px", textAlign: "left" }}>S.No</th>
                    <th style={{ textAlign: "left" }}>Center / User</th>
                    <th style={{ textAlign: "left" }}>Login Time</th>
                    <th style={{ textAlign: "left" }}>Last Activity</th>
                    <th style={{ textAlign: "center", width: "100px" }}>Candidates</th>
                    <th style={{ textAlign: "center", width: "100px" }}>Aadhaar</th>
                    <th style={{ textAlign: "center", width: "100px" }}>DL</th>
                    <th style={{ textAlign: "center", width: "100px" }}>OCR</th>
                    <th style={{ textAlign: "center", width: "100px" }}>Biometric</th>
                    <th style={{ textAlign: "center", width: "140px" }}>Avg Reg Time</th>
                    <th style={{ textAlign: "center", width: "110px" }}>Status</th>
                    <th style={{ textAlign: "center", width: "120px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCenters.length > 0 ? (
                    paginatedCenters.map((c, index) => (
                      <tr key={c.branchId || index}>
                        {/* S.NO */}
                        <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, color: "var(--slate)" }}>
                          {page * rowsPerPage + index + 1}
                        </td>

                        {/* CENTER / USER */}
                        <td>
                          <div className="font-bold text-cyan-900">{c.name}</div>
                          <div className="text-xs text-slate-400">{c.location}</div>
                        </td>

                        {/* LOGIN TIME */}
                        <td style={{ color: "var(--slate)" }}>
                          {c.loginTime ? formatTimeOnly(c.loginTime) : "-"}
                        </td>

                        {/* LAST ACTIVITY */}
                        <td style={{ color: "var(--slate)" }}>
                          {c.lastActivity ? formatTimeOnly(c.lastActivity) : "-"}
                        </td>

                        {/* CANDIDATES */}
                        <td style={{ textAlign: "center", fontWeight: "bold" }}>
                          {c.candidates}
                        </td>

                        {/* AADHAAR */}
                        <td style={{ textAlign: "center", color: "var(--slate)" }}>
                          {c.aadhaar}
                        </td>

                        {/* DL */}
                        <td style={{ textAlign: "center", color: "var(--slate)" }}>
                          {c.dl}
                        </td>

                        {/* OCR */}
                        <td style={{ textAlign: "center", fontWeight: 600 }}>
                          {c.ocr}
                        </td>

                        {/* BIOMETRIC */}
                        <td style={{ textAlign: "center", color: "var(--slate)" }}>
                          {c.biometric}
                        </td>

                        {/* AVG TIME */}
                        <td style={{ textAlign: "center", fontWeight: 700, color: "var(--brass-deep)" }}>
                          {c.avgCandidateTimeMs ? formatDuration(c.avgCandidateTimeMs) : "-"}
                        </td>

                        {/* STATUS */}
                        <td style={{ textAlign: "center" }}>
                          <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full border ${
                            c.isActive 
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                              : "bg-slate-50 text-slate-400 border-slate-200"
                          }`}>
                            {c.status}
                          </span>
                        </td>

                        {/* ACTION */}
                        <td style={{ textAlign: "center" }}>
                          <button
                            onClick={() => handleViewCenter(c)}
                            style={{
                              padding: "6px 12px",
                              background: "var(--teal-soft)",
                              color: "var(--teal-deep)",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "11px",
                              fontWeight: 700,
                              fontFamily: "'IBM Plex Mono', monospace",
                              textTransform: "uppercase"
                            }}
                          >
                            VIEW
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={12} className="py-10 text-center text-slate-400 font-medium">
                        No performance records found matching the active filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <TablePagination
                component="div"
                count={filteredCenters.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
              />
            </div>
          </>
        )}

        {/* --- CHRONOLOGICAL TIMELINE DRAWER --- */}
        {drawerOpen && (
          <div className="fixed inset-0 z-50 overflow-hidden" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity" onClick={() => setDrawerOpen(false)}></div>
            
            <div className="absolute inset-y-0 right-0 max-w-full flex pl-10">
              <div className="w-screen max-w-4xl bg-white shadow-2xl flex flex-col h-full transform transition duration-300">
                
                {/* Drawer Header */}
                <div className="px-6 py-5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-cyan-900 flex items-center gap-2" style={{ fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>
                      <FaBuilding className="text-cyan-700" />
                      <span>{selectedCenter?.name} — Full Day Activity</span>
                    </h2>
                    <p className="text-xs text-slate-400 mt-1">Location: {selectedCenter?.location} | Date: {new Date(selectedDate).toLocaleDateString("en-IN")}</p>
                  </div>
                  
                  <button 
                    onClick={() => setDrawerOpen(false)}
                    className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-200 transition"
                  >
                    <FaTimes size={18} />
                  </button>
                </div>

                {drawerLoading ? (
                  <div className="flex-1 flex justify-center items-center">
                    <div className="w-10 h-10 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto flex flex-col">
                    
                    {/* Center metrics summary strip */}
                    <div className="grid grid-cols-5 border-b border-slate-200 bg-slate-50/50 text-center py-4">
                      <div>
                        <div className="text-[10px] text-slate-400 font-bold uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Login</div>
                        <div className="font-semibold text-slate-700 mt-1">{selectedCenter?.loginTime ? formatTimeOnly(selectedCenter.loginTime) : "-"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-slate-400 font-bold uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Last Active</div>
                        <div className="font-semibold text-slate-700 mt-1">{selectedCenter?.lastActivity ? formatTimeOnly(selectedCenter.lastActivity) : "-"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-slate-400 font-bold uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Candidates</div>
                        <div className="font-semibold text-slate-700 mt-1">{selectedCenter?.candidates || 0}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-slate-400 font-bold uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Total OCR</div>
                        <div className="font-semibold text-slate-700 mt-1">{selectedCenter?.ocr || 0}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-slate-400 font-bold uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Avg Reg Time</div>
                        <div className="font-semibold text-cyan-700 mt-1">{selectedCenter?.avgCandidateTimeMs ? formatDuration(selectedCenter.avgCandidateTimeMs) : "-"}</div>
                      </div>
                    </div>

                    {/* Drawer search & filter tabs */}
                    <div className="px-6 py-4 border-b border-slate-200 bg-white sticky top-0 z-10 flex flex-col md:flex-row md:items-center justify-between gap-3">
                      {/* Filter Tabs */}
                      <div className="flex flex-wrap gap-1.5">
                        {["All", "Registration", "Aadhaar", "DL", "OCR", "Biometric", "Login"].map((tab) => (
                          <button
                            key={tab}
                            onClick={() => setDrawerFilterTab(tab)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                              drawerFilterTab === tab
                                ? "bg-cyan-700 text-white shadow-sm"
                                : "bg-slate-100 hover:bg-slate-200 text-slate-600"
                            }`}
                            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                          >
                            {tab}
                          </button>
                        ))}
                      </div>

                      {/* Search Field */}
                      <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search Candidate ID / Name / Action..."
                          value={drawerSearch}
                          onChange={(e) => setDrawerSearch(e.target.value)}
                          className="input-field pl-9 w-full md:w-64"
                          style={{ height: "35px", fontSize: "12px" }}
                        />
                      </div>
                    </div>

                    {/* TAB: CANDIDATE PERFORMANCE MATRIX TABLE */}
                    {candidatePerformanceData.length > 0 && (
                      <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/50">
                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                          <FaUserCheck className="text-cyan-700" />
                          <span>Candidate Performance View</span>
                        </div>
                        <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white shadow-sm">
                          <table className="min-w-full text-slate-700 text-xs">
                            <thead className="bg-slate-100 border-b border-slate-200 text-slate-550 font-bold uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                              <tr>
                                <th className="px-3 py-2 text-left">Candidate Name</th>
                                <th className="px-3 py-2 text-left">ID</th>
                                <th className="px-3 py-2 text-center">Start</th>
                                <th className="px-3 py-2 text-center">End</th>
                                <th className="px-3 py-2 text-center">Total Time</th>
                                <th className="px-3 py-2 text-center">Aadhaar OCR</th>
                                <th className="px-3 py-2 text-center">DL OCR</th>
                                <th className="px-3 py-2 text-center">Biometric</th>
                                <th className="px-3 py-2 text-center">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {candidatePerformanceData.map(c => (
                                <tr key={c.candidateId} className="hover:bg-slate-50/50">
                                  <td className="px-3 py-2 font-semibold text-slate-800">{c.candidateName}</td>
                                  <td className="px-3 py-2 font-mono text-slate-400">{c.candidateId.substring(0, 8)}...</td>
                                  <td className="px-3 py-2 text-center">{c.registrationStart ? formatTimeOnly(c.registrationStart) : "-"}</td>
                                  <td className="px-3 py-2 text-center">{c.registrationEnd ? formatTimeOnly(c.registrationEnd) : "-"}</td>
                                  <td className="px-3 py-2 text-center font-bold text-cyan-700">{c.totalTime ? formatDuration(c.totalTime) : "-"}</td>
                                  <td className="px-3 py-2 text-center text-slate-500">{c.aadhaarOcrTime ? formatDuration(c.aadhaarOcrTime) : "-"}</td>
                                  <td className="px-3 py-2 text-center text-slate-500">{c.dlOcrTime ? formatDuration(c.dlOcrTime) : "-"}</td>
                                  <td className="px-3 py-2 text-center text-slate-500">{c.biometricTime ? formatDuration(c.biometricTime) : "-"}</td>
                                  <td className="px-3 py-2 text-center">
                                    <span className={`px-2 py-0.5 font-bold rounded-full text-[10px] ${
                                      c.status === "Completed"
                                        ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
                                        : c.status === "Abandoned"
                                        ? "bg-amber-50 text-amber-600 border border-amber-200"
                                        : "bg-blue-50 text-blue-600 border border-blue-200"
                                    }`}>
                                      {c.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Chronological Log list */}
                    <div className="px-6 py-6 flex-1 bg-slate-50/20">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        <FaHistory className="text-cyan-700" />
                        <span>Chronological Activity Timeline</span>
                      </div>

                      {filteredTimelineLogs.length > 0 ? (
                        <div className="relative border-l border-slate-200 ml-4 pl-6 space-y-6">
                          {filteredTimelineLogs.map((log) => {
                            const hasCandidate = log.candidate;
                            const candName = log.candidate?.name || log.metadata?.name;
                            const isSuccess = log.success !== null ? log.success : true;

                            return (
                              <div key={log._id} className="relative">
                                {/* Indicator dot */}
                                <span className={`absolute -left-[31px] top-1.5 flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-white ${
                                  log.action.endsWith("FAILED") || log.success === false
                                    ? "bg-rose-500 text-white"
                                    : "bg-cyan-500 text-white"
                                }`} />

                                {/* Log Card */}
                                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-cyan-300 transition">
                                  <div className="space-y-1">
                                    <div className="flex items-center flex-wrap gap-2">
                                      <span className="text-[11px] font-bold text-slate-400 font-mono">
                                        {new Date(log.createdAt).toLocaleTimeString("en-IN", { hour12: false })}
                                      </span>
                                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${
                                        ACTION_COLORS[log.action] || "bg-slate-100 text-slate-700 border-slate-200"
                                      }`}>
                                        {log.action}
                                      </span>
                                      {log.durationMs !== null && (
                                        <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                                          Duration: {formatDuration(log.durationMs)}
                                        </span>
                                      )}
                                      {!isSuccess && (
                                        <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-200 flex items-center gap-1">
                                          <FaExclamationTriangle size={10} /> Failed
                                        </span>
                                      )}
                                    </div>

                                    <div className="text-sm font-medium text-slate-700">
                                      {log.action === "LOGIN" && <span>Center logged in</span>}
                                      {log.action === "LOGOUT" && <span>Center logged out</span>}
                                      {log.action === "LOGIN_FAILED" && <span className="text-rose-600">Failed login attempt</span>}
                                      {log.action === "REGISTRATION_STARTED" && <span>Candidate registration workflow initiated.</span>}
                                      {log.action === "REGISTRATION_COMPLETED" && (
                                        <span>
                                          Candidate registration successfully submitted. Total time: <strong>{formatDuration(log.durationMs)}</strong>
                                        </span>
                                      )}
                                      {log.action === "REGISTRATION_ABANDONED" && (
                                        <span className="text-amber-600">
                                          Registration abandoned. Form closed after <strong>{formatDuration(log.durationMs)}</strong>
                                        </span>
                                      )}
                                      {log.action === "CANDIDATE_CREATED" && <span>Profile record created for Candidate.</span>}
                                      {log.action === "AADHAAR_SCAN_STARTED" && <span>Aadhaar document front/back scan uploaded.</span>}
                                      {log.action === "AADHAAR_OCR_STARTED" && <span>Extracting details from Aadhaar image...</span>}
                                      {log.action === "AADHAAR_OCR_COMPLETED" && (
                                        <span className="text-emerald-700 font-medium">Aadhaar OCR processing complete. Extracted demographic details.</span>
                                      )}
                                      {log.action === "AADHAAR_OCR_FAILED" && (
                                        <span className="text-rose-700 font-medium">Aadhaar OCR processing failed: {log.metadata?.error || "Unknown Error"}</span>
                                      )}
                                      {log.action === "DL_SCAN_STARTED" && <span>Driving Licence document front/back scan uploaded.</span>}
                                      {log.action === "DL_OCR_STARTED" && <span>Extracting details from Driving Licence image...</span>}
                                      {log.action === "DL_OCR_COMPLETED" && (
                                        <span className="text-emerald-700 font-medium">Driving Licence OCR processing complete. Extracted licensing details.</span>
                                      )}
                                      {log.action === "DL_OCR_FAILED" && (
                                        <span className="text-rose-700 font-medium">Driving Licence OCR processing failed: {log.metadata?.error || "Unknown Error"}</span>
                                      )}
                                      {log.action === "BIOMETRIC_STARTED" && <span>Live face scan capture starting.</span>}
                                      {log.action === "BIOMETRIC_COMPLETED" && (
                                        <span className="text-emerald-700 font-medium">
                                          Biometric match confirmed! {log.metadata?.info || "Face template saved"}
                                        </span>
                                      )}
                                      {log.action === "BIOMETRIC_FAILED" && (
                                        <span className="text-rose-700 font-medium">Biometric scan failed: {log.metadata?.error || "No face detected"}</span>
                                      )}
                                    </div>

                                    {hasCandidate && (
                                      <div className="text-xs font-bold text-cyan-800 bg-cyan-50 px-2 py-0.5 rounded inline-block mt-1">
                                        Candidate: {candName} ({log.candidate?._id || log.candidate})
                                      </div>
                                    )}
                                  </div>

                                  <div className="text-right whitespace-nowrap text-xs text-slate-405">
                                    <div>User: <strong>{log.username}</strong></div>
                                    <div className="text-[10px] text-slate-350">{log.role}</div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-400 text-sm">
                          No activity records matching filters on this day.
                        </div>
                      )}
                    </div>

                  </div>
                )}

              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default DailyActivity;
